import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { CountrySearchDialog, CustomsWarningCard, useGeoCountries } from "@/components/GeoDialogs";
import { ProhibitedNotice, hasProhibitedAck } from "@/components/ProhibitedGoods";
import { DestCity } from "@/components/DestCityCI";
import { ModeDeparture } from "@/components/ModeDeparture";
import { HubAddress } from "@/components/HubAddress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Globe, MapPin, Search, Plus, Trash2, Plane, Ship, ArrowRight, Warehouse } from "lucide-react";

const GOODS_COUNTRIES = ["Royaume-Uni", "France", "Allemagne", "Espagne", "Belgique", "Italie", "Pays-Bas", "USA", "Chine", "Turquie", "Émirats (Dubaï)", "Autre pays"];
import { curSym } from "@/lib/currency";
const fmt = (n) => Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 4 });

export default function GoodsPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [goodsCountry, setGoodsCountry] = useState("");
  const [route, setRoute] = useState(null);
  const [hubId, setHubId] = useState("");
  const [dests, setDests] = useState([]);
  const [destCountry, setDestCountry] = useState("");
  const [destCity, setDestCity] = useState("");
  const [mode, setMode] = useState("air");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [weightKg, setWeightKg] = useState("");
  const [tracking, setTracking] = useState("");
  const [holder, setHolder] = useState("");
  const [prohibitedOk, setProhibitedOk] = useState(hasProhibitedAck());
  const [instr, setInstr] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [departureId, setDepartureId] = useState("");
  const [depInfo, setDepInfo] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ name: "", qty: 1, weight_est: "", container: "", length_cm: "", width_cm: "", height_cm: "", description: "" });
  // Tarif CBM du trajet sélectionné — affiché dans le formulaire « Ajouter mon article »
  const [cbmRate, setCbmRate] = useState(null);
  useEffect(() => {
    if (!customOpen || !route?.hub_country || !destCountry || !mode) { setCbmRate(null); return; }
    api.get(`/goods/cbm-rate?hub_country=${encodeURIComponent(route.hub_country)}&dest_country=${encodeURIComponent(destCountry)}&mode=${mode}`)
      .then(({ data }) => setCbmRate(data)).catch(() => setCbmRate(null));
  }, [customOpen, route, destCountry, mode]);
  const [busy, setBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [customsAck, setCustomsAck] = useState(false);
  const geo = useGeoCountries();
  const cLabel = (name) => {
    const k = `ctry.${name}`, v = t(k);
    if (v !== k) return v;
    const g = geo.find((x) => x.name === name);
    return lang === "en" && g ? g.name_en : name;
  };

  useEffect(() => { api.get("/countries?kind=destination").then(({ data }) => setDests(data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 2) api.get(`/catalog/search?q=${encodeURIComponent(q)}&mode=${mode}`).then(({ data }) => setResults(data));
      else setResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode]);

  // Le client choisit d'abord le mode — les articles incompatibles sont retirés du panier
  const changeMode = (m) => {
    setMode(m); setEstimate(null); setResults([]);
    setCart((c) => c.filter((x) => x.custom || !x.allowed_modes || x.allowed_modes === "both" || x.allowed_modes === m));
  };

  const pick = async (c) => {
    setGoodsCountry(c); setRoute(null); setHubId(""); setEstimate(null); setCustomsAck(false);
    try {
      const { data } = await api.get(`/goods/route?goods_country=${encodeURIComponent(c)}`);
      setRoute(data);
      if (data.hubs.length > 0) setHubId(data.hubs[0].id);
    } catch { toast.error("Erreur de routage"); }
  };

  const pickDest = (name) => {
    setDestCountry(name); setDestCity(""); setEstimate(null);
  };

  const addArticle = (a) => {
    setCart((c) => {
      const ex = c.find((x) => x.article_id === a.id);
      if (ex) return c.map((x) => (x.article_id === a.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { article_id: a.id, name: a.name_fr, qty: 1, allowed_modes: a.allowed_modes || "both" }];
    });
    setQ(""); setResults([]); setEstimate(null);
  };

  const addCustom = async () => {
    if (!custom.name.trim()) { toast.error("Nom de l'article requis"); return; }
    const payload = { ...custom, qty: Number(custom.qty) || 1, weight_est: Number(custom.weight_est) || 0,
      length_cm: Number(custom.length_cm) || 0, width_cm: Number(custom.width_cm) || 0, height_cm: Number(custom.height_cm) || 0,
      container: custom.container || "",
      hub_country: route?.hub_country || "", dest_country: destCountry, dest_city: destCity, mode };
    if (user?.role === "customer") { try { await api.post("/catalog/unlisted", payload); } catch { /* non bloquant */ } }
    setCart((c) => [...c, { custom: payload, name: `${custom.name} (non répertorié)`, qty: payload.qty, unlisted: true }]);
    setCustomOpen(false); setCustom({ name: "", qty: 1, weight_est: "", container: "", length_cm: "", width_cm: "", height_cm: "", description: "" }); setEstimate(null);
    toast.success("Article ajouté — non répertorié, il sera confirmé par devis");
  };

  // Validation commune de TOUS les champs obligatoires (estimation, création, paiement)
  const [fieldErrors, setFieldErrors] = useState({});
  useEffect(() => {
    setFieldErrors((e) => ({ ...e,
      hub: hubId ? undefined : e.hub,
      destCountry: destCountry ? undefined : e.destCountry,
      destCity: destCity ? undefined : e.destCity,
      mode: mode ? undefined : e.mode,
      cargo: (cart.length || Number(weightKg)) ? undefined : e.cargo }));
  }, [hubId, destCountry, destCity, mode, cart, weightKg]);
  const validatePage = () => {
    const errs = {};
    if (!route || !goodsCountry) errs.country = "Sélectionnez le pays de départ";
    if (!hubId) errs.hub = "Sélectionnez un hub de départ";
    if (!destCountry) errs.destCountry = "Sélectionnez un pays de destination";
    if (!destCity) errs.destCity = "Sélectionnez une ville de destination";
    if (!mode) errs.mode = "Choisissez un mode de transport";
    if (cart.length === 0 && !Number(weightKg)) errs.cargo = "Ajoutez au moins un article ou un poids estimé";
    setFieldErrors(errs);
    const order = ["country", "hub", "destCountry", "destCity", "mode", "cargo"];
    const first = order.find((k) => errs[k]);
    if (first) {
      const anchors = { country: "goods-step-country", hub: "goods-step-hub", destCountry: "field-dest-country",
        destCity: "field-dest-city", mode: "field-mode", cargo: "field-cargo" };
      // remonter jusqu'au premier champ incorrect, dans l'ordre de la page
      setTimeout(() => document.getElementById(anchors[first])?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    }
    return !first;
  };

  const runEstimate = async () => {
    // Jamais d'estimation sans hub, pays, ville sélectionnée, mode et marchandise
    if (!validatePage()) { setEstimate(null); return; }
    try {
      const items = cart.map((x) => (x.custom ? { custom: x.custom, qty: x.qty }
        : { article_id: x.article_id, qty: x.qty, size: x.size ? Number(x.size) : null }));
      // Moteur Catalogue & Tarifs uniquement (5A-2A) — aucune règle codée en dur
      const { data: o } = await api.post("/goods/estimate", {
        hub_country: route.hub_country, dest_country: destCountry, dest_city: destCity,
        mode, items, weight_kg: Number(weightKg) || 0,
      });
      // Ne jamais inventer de prix : si aucune ligne tarifée, afficher Sur devis
      const priced = o && ((o.lines || []).some((l) => l.status === "priced") || (o.weight_line && o.weight_line.status === "priced"));
      setEstimate(o && priced && o.total > 0 ? o : { none: true, lines: o?.lines || [] });
    } catch { setEstimate({ none: true }); }
  };

  // 5.3A — Brouillon automatique (7 jours, même navigateur) après estimation
  const DRAFT_KEY = "c2g_goods_draft";
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (d && Date.now() - d.ts < 7 * 24 * 3600 * 1000 && d.goodsCountry) setDraft(d);
      else localStorage.removeItem(DRAFT_KEY);
    } catch { /* brouillon illisible */ }
  }, []);
  useEffect(() => {
    if (!goodsCountry) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      ts: Date.now(), goodsCountry, destCountry, destCity, mode, departureId, depInfo,
      cart, weightKg, tracking, holder, estimate,
    }));
  }, [goodsCountry, destCountry, destCity, mode, departureId, depInfo, cart, weightKg, tracking, holder, estimate]);
  const resumeDraft = async () => {
    const d = draft;
    setDraft(null);
    await pick(d.goodsCountry);
    setDestCountry(d.destCountry || ""); setDestCity(d.destCity || "");
    setMode(d.mode || "air"); setDepartureId(d.departureId || ""); setDepInfo(d.depInfo || null);
    setCart(d.cart || []); setWeightKg(d.weightKg || ""); setTracking(d.tracking || "");
    setHolder(d.holder || ""); setEstimate(d.estimate || null);
  };

  const submit = async () => {
    if (!user) {
      // Le brouillon est déjà sauvegardé — connexion puis retour ici sans rien ressaisir
      sessionStorage.setItem("redirect_after_login", "/goods");
      navigate("/login");
      return;
    }
    if (!holder) { toast.error(t("goods.holderFirst")); return; }
    if (user.role !== "customer") { toast.error("Connectez-vous en tant que client"); return; }
    // Même validation commune que l'estimation : tous les champs obligatoires
    if (!validatePage()) return;
    if (cart.length === 0 && !Number(weightKg)) { toast.error("Décrivez votre marchandise (article ou poids)"); return; }
    setBusy(true);
    try {
      const items = cart.map((x) => ({ name: x.name, qty: x.qty, article_id: x.article_id || "", unlisted: !!x.unlisted }));
      const desc = items.map((it) => `${it.qty}× ${it.name}`).join(", ") + (Number(weightKg) ? ` + ${weightKg} kg` : "");
      const { data } = await api.post("/shipments/hub", {
        hub_id: hubId, dest_country: destCountry, dest_city: destCity, mode,
        goods_description: desc, goods_country: goodsCountry, items, tracking_number: tracking, holder,
        departure_id: departureId, weight_kg: Number(weightKg) || 0,
        estimate_total: estimate && !estimate.none && estimate.total > 0 ? estimate.total : 0,
      });
      toast.success(`Expédition ${data.reference} créée — envoyez votre marchandise au ${data.hub_name}`);
      localStorage.removeItem(DRAFT_KEY);
      setDraft(null);
      setInstr(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création"); }
    finally { setBusy(false); }
  };

  const selectedHub = route?.hubs?.find((h) => h.id === hubId);
  const routeReady = route && (!route.customs_warning || customsAck);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="bg-primary text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 border border-accent/50 bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent mb-4">
            <Boxes className="h-3.5 w-3.5" /> {t("goods.badge")}
          </div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">{t("goods.title")}</h1>
          <p className="mt-3 text-white/80 max-w-2xl">{t("goods.subtitle")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        {/* Reprendre le brouillon conservé 7 jours */}
        {draft && !goodsCountry && (
          <div className="mb-5 border border-accent bg-accent/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="goods-draft-banner">
            <div className="text-sm">
              <b>Estimation en cours trouvée</b> — {cLabel(draft.goodsCountry)}{draft.destCountry ? ` → ${cLabel(draft.destCountry)}` : ""}{draft.destCity ? ` (${draft.destCity})` : ""}
            </div>
            <div className="flex gap-2">
              <Button data-testid="goods-draft-resume" size="sm" className="rounded-sm bg-accent text-white" onClick={resumeDraft}>Continuer avec l'estimation précédente</Button>
              <Button data-testid="goods-draft-discard" size="sm" variant="outline" className="rounded-sm" onClick={() => { localStorage.removeItem(DRAFT_KEY); setDraft(null); }}>Faire une nouvelle estimation</Button>
            </div>
          </div>
        )}
        {/* Étape 1 : où se trouve la marchandise (pays d'origine) */}
        <h2 className="font-heading font-bold text-lg flex items-center gap-2" id="goods-step-country"><Globe className="h-5 w-5 text-accent" /> {t("goods.step1")}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {GOODS_COUNTRIES.map((c) => {
            const active = goodsCountry === c || (c === "Autre pays" && !!goodsCountry && !GOODS_COUNTRIES.includes(goodsCountry));
            return (
              <button key={c} data-testid={`goods-country-${c}`} onClick={() => (c === "Autre pays" ? setSearchOpen(true) : pick(c))}
                className={`px-4 py-2 text-sm font-semibold border transition-colors ${active ? "bg-primary text-white border-primary" : "border-border bg-card hover:border-accent"}`}>
                {c === "Autre pays" && active ? cLabel(goodsCountry) : t(`ctry.${c}`)}
              </button>
            );
          })}
        </div>

        {/* Étape 2 : Hub recommandé */}
        {route && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Warehouse className="h-5 w-5 text-accent" /> {t("goods.step2")}</h2>
            <p className="mt-2 font-semibold text-accent" data-testid="goods-route-message">{t("goods.routeMsg").replace("{label}", route.hub_country === "France" ? "France" : "UK")}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground" data-testid="goods-chain">
              {[cLabel(goodsCountry), `Colis2Go ${route.hub_country === "France" ? "France" : "UK"}`, t("goods.chainCheck"), t("goods.chainShip")].map((s, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-accent" />}
                  <span className={i === 1 ? "font-semibold text-foreground" : ""}>{s}</span>
                </span>
              ))}
            </div>
            {route.customs_warning && !customsAck && <CustomsWarningCard onConfirm={() => setCustomsAck(true)} />}
            {routeReady && (<>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" id="goods-step-hub">
              {route.hubs.map((h) => (
                <button key={h.id} data-testid={`goods-hub-${h.id}`} onClick={() => setHubId(h.id)}
                  className={`text-left border bg-card p-4 transition-colors ${hubId === h.id ? "border-accent ring-1 ring-accent" : "border-border hover:border-accent"}`}>
                  <div className="font-heading font-bold">{h.name}</div>
                  <div className="mt-1"><HubAddress hub={h} /></div>
                </button>
              ))}
              {route.hubs.length === 0 && <p className="text-sm text-muted-foreground col-span-3">{t("goods.noHub")}</p>}
            </div>
            {route.hubs.length > 1 && <p className="mt-2 text-xs text-muted-foreground">{t("goods.multiHub")}</p>}
            <p className="mt-2 text-xs text-muted-foreground">{t("goods.addrLater")}</p>
            </>)}
          </div>
        )}

        {/* Étape 3 : destination + mode */}
        {routeReady && hubId && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg">{t("goods.step3")}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3 max-w-3xl">
              <div id="field-dest-country" className={fieldErrors.destCountry ? "ring-2 ring-red-500 rounded-sm" : ""}>
                <Select value={destCountry} onValueChange={pickDest}>
                  <SelectTrigger data-testid="goods-dest-country" className="rounded-sm"><SelectValue placeholder={t("goods.destCountry")} /></SelectTrigger>
                  <SelectContent>{dests.map((c) => <SelectItem key={c.id} value={c.name}>{c.flag} {cLabel(c.name)}</SelectItem>)}</SelectContent>
                </Select>
                {fieldErrors.destCountry && <p className="mt-1 text-xs font-medium text-red-600" data-testid="err-dest-country">{fieldErrors.destCountry}</p>}
              </div>
              {destCountry && (
                <div id="field-dest-city" className={fieldErrors.destCity ? "ring-2 ring-red-500 rounded-sm" : ""}>
                  <DestCity country={dests.find((c) => c.name === destCountry)}
                    value={destCity} onChange={setDestCity} testid="goods-city" />
                  {fieldErrors.destCity && <p className="mt-1 text-xs font-medium text-red-600" data-testid="err-dest-city">Sélectionnez une ville de destination</p>}
                </div>
              )}
            </div>
            {/* Mode + départ planifié — composant UNIQUE (Phase 4), filtré Hub + Destination + Mode */}
            {destCountry && (
              <div id="field-mode" className={`mt-3 max-w-3xl ${fieldErrors.mode ? "ring-2 ring-red-500 rounded-sm" : ""}`}>
                <ModeDeparture hubCountry={route?.hub_country} destCountry={destCountry}
                  mode={mode} onModeChange={changeMode}
                  departureId={departureId}
                  onDepartureChange={(id, d) => { setDepartureId(id); setDepInfo(d || null); }} testid="goods" />
                {fieldErrors.mode && <p className="mt-1 text-xs font-medium text-red-600" data-testid="err-mode">{fieldErrors.mode}</p>}
              </div>
            )}
          </div>
        )}

        {/* Étape 6 : marchandise — le départ est facultatif (informatif, non bloquant) */}
        {routeReady && hubId && destCountry && mode && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg">{t("goods.step4")}</h2>
            <div className="mt-3 relative max-w-3xl">
              <div className="flex items-center gap-2 border border-border bg-card px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input data-testid="goods-search" className="border-0 focus-visible:ring-0 rounded-none" placeholder={t("goods.searchPh")} value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full border border-border bg-card shadow-lg max-h-72 overflow-y-auto" data-testid="goods-results">
                  {results.map((a) => (
                    <button key={a.id} data-testid={`goods-add-${a.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between" onClick={() => addArticle(a)}>
                      <span>{a.name_fr}</span>
                      <span className="flex gap-1">
                        {a.quote_required && <Badge className="rounded-sm bg-amber-100 text-amber-700 text-[10px]">{t("common.onQuote")}</Badge>}
                        <Plus className="h-4 w-4 text-accent" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {q.trim().length >= 2 && results.length === 0 && (
                <div className="mt-2 w-full border border-border bg-card p-3 text-sm" data-testid="goods-notfound-box">
                  <p className="font-semibold">Article introuvable</p>
                  <p className="text-muted-foreground text-xs">{t("goods.notInCatalog")}</p>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 max-w-3xl">
              <span className="text-sm text-muted-foreground" data-testid="goods-notfound-text">Vous ne trouvez pas votre article ?</span>
              <Button size="sm" variant="outline" data-testid="goods-notfound-add" className="rounded-sm gap-1 border-accent text-accent hover:bg-accent hover:text-white" onClick={() => { setCustom((c) => ({ ...c, name: q })); setCustomOpen(true); }}><Plus className="h-4 w-4" /> {t("goods.addCustom")}</Button>
            </div>
            <div className="mt-4 border border-border bg-card p-4 max-w-3xl">
              <h3 className="font-heading font-bold text-sm">{t("goods.yourShipment")}</h3>
              <div className="mt-3 space-y-2" data-testid="goods-cart">
                {cart.map((x, i) => (
                  <div key={i} className="border border-border p-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{x.name}</span>
                      <span className="flex items-center gap-2">
                        <Input data-testid={`goods-qty-${i}`} type="number" min="1" className="rounded-sm h-8 w-16" value={x.qty}
                          onChange={(e) => { const v = Math.max(1, Number(e.target.value) || 1); setCart((c) => c.map((y, j) => (j === i ? { ...y, qty: v } : y))); setEstimate(null); }} />
                        <button data-testid={`goods-remove-${i}`} onClick={() => { setCart((c) => c.filter((_, j) => j !== i)); setEstimate(null); }}><Trash2 className="h-4 w-4 text-red-500" /></button>
                      </span>
                    </div>
                    {(x.question || estimate?.lines?.find((l) => l.article_id === x.article_id)?.question) && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <Label className="text-xs text-amber-700">{x.question || estimate.lines.find((l) => l.article_id === x.article_id).question}</Label>
                        <Input data-testid={`goods-size-${i}`} type="number" className="rounded-sm h-8 w-24" placeholder='ex : 50'
                          value={x.size || ""} onChange={(e) => { const v = e.target.value; setCart((c) => c.map((y, j) => (j === i ? { ...y, size: v, question: y.question || estimate?.lines?.find((l) => l.article_id === y.article_id)?.question } : y))); setEstimate(null); }} />
                      </div>
                    )}
                  </div>
                ))}
                {cart.length === 0 && <p className="text-xs text-muted-foreground">{t("goods.emptyCart")}</p>}
              </div>
              <div className="mt-3 flex flex-wrap items-end gap-3" id="field-cargo">
                <div><Label className="text-xs">{t("goods.weight")}</Label><Input data-testid="goods-weight" type="number" className="rounded-sm w-36" placeholder="ex : 25" value={weightKg} onChange={(e) => { setWeightKg(e.target.value); setEstimate(null); }} /></div>
                <div><Label className="text-xs">{t("goods.tracking")}</Label><Input data-testid="goods-tracking" className="rounded-sm w-48" value={tracking} onChange={(e) => setTracking(e.target.value)} /></div>
                <Button variant="outline" data-testid="goods-estimate-btn" className="rounded-sm" onClick={runEstimate}>{t("goods.estimateBtn")}</Button>
              </div>
              {fieldErrors.cargo && <p className="mt-1 text-xs font-medium text-red-600" data-testid="err-cargo">{fieldErrors.cargo}</p>}
              {estimate && !estimate.none && (
                <div className="mt-3 border border-accent/40 bg-accent/5 p-3 text-sm" data-testid="goods-estimate">
                  <div className="font-heading font-bold text-xs uppercase tracking-wider text-accent">Estimation du transport</div>
                  <div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs" data-testid="goods-estimate-recap">
                    <span className="text-muted-foreground">Hub :</span><span className="font-medium">{selectedHub?.name || `Colis2Go ${route?.hub_country === "France" ? "France" : "UK"}`}</span>
                    <span className="text-muted-foreground">Destination :</span><span className="font-medium">{cLabel(destCountry)}{destCity ? ` — ${destCity}` : ""}</span>
                    <span className="text-muted-foreground">Mode :</span><span className="font-medium">{mode === "air" ? t("search.air") : t("search.sea")}</span>
                    {depInfo && <><span className="text-muted-foreground">Départ :</span><span className="font-medium">{depInfo.departure_date}</span></>}
                  </div>
                  <div className="mt-2 pt-2 border-t border-accent/20 space-y-1.5">
                    {[...(estimate.lines || []), ...(estimate.weight_line ? [estimate.weight_line] : [])].map((l, i) => (
                      <div key={i} className="flex items-start justify-between gap-3" data-testid={`est-line-${i}`}>
                        <span>
                          <span className="font-medium">{l.name}</span>
                          <span className="text-muted-foreground"> · {l.method || (l.name.includes("kg") ? "Prix / kg" : "")}
                            {l.unit_price != null && ` · ${l.qty > 1 ? `${l.qty} × ` : ""}${l.unit_price} ${estimate.currency}`}
                            {l.cbm != null && ` · Volume : ${l.cbm} m³`}
                          </span>
                        </span>
                        <span className="font-semibold whitespace-nowrap">
                          {l.line_total != null ? `${l.line_total} ${estimate.currency}` : <span className="text-amber-700">{l.question ? "Précisez ci-dessus" : "Devis requis"}</span>}
                        </span>
                      </div>
                    ))}
                    {(estimate.lines || []).some((l) => l.line_total != null && (l.method || "").includes("unité")) && (
                      <p className="text-[11px] text-muted-foreground" data-testid="fixed-price-note">Le prix fixe de l&rsquo;article est prioritaire — aucun calcul CBM n&rsquo;est ajouté.</p>
                    )}
                  </div>
                  <div className="mt-2 pt-2 border-t border-accent/30 flex items-center justify-between font-heading font-bold" data-testid="goods-estimate-total">
                    <span>TOTAL ESTIMÉ</span>
                    <span className="text-accent text-lg">{estimate.total} {estimate.currency}</span>
                  </div>
                  {estimate.quote_needed > 0 && <p className="mt-1 text-xs text-amber-700">{estimate.quote_needed} article(s) « Devis requis » — Colis2Go vous confirmera leur prix.</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{t("goods.estimateNote")}</p>
                </div>
              )}
              {estimate?.none && <p className="mt-3 text-sm text-muted-foreground" data-testid="goods-estimate-none">{t("goods.quoteOnly")}</p>}
            </div>

            {/* Étape 8 : après estimation — chez qui se trouve la marchandise ? */}
            {estimate && (
              <div className="mt-6 max-w-3xl">
                <h2 className="font-heading font-bold text-lg" data-testid="goods-holder-q">{t("holder.q")}</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[["self", t("holder.self")], ["supplier", t("holder.supplier")], ["relative", t("holder.relative")]].map(([v, l]) => (
                    <button key={v} data-testid={`goods-holder-${v}`} onClick={() => setHolder(v)}
                      className={`px-4 py-2 text-sm font-semibold border transition-colors ${holder === v ? "bg-primary text-white border-primary" : "border-border bg-card hover:border-accent"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Étape 9 : bouton final — après le choix ci-dessus */}
            {estimate && holder && (
            <div className="mt-6 border-2 border-accent bg-accent/5 p-5 max-w-3xl flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading font-bold">{selectedHub ? `${t("goods.sendTo")} ${selectedHub.name}` : t("goods.ready")}</div>
                <p className="text-sm text-muted-foreground">{t("goods.readyDesc")} {destCity || destCountry}.</p>
              </div>
              <ProhibitedNotice value={prohibitedOk} onChange={setProhibitedOk} />
              <Button data-testid="goods-submit" className="mt-2 rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" onClick={submit} disabled={busy || (!!user && !prohibitedOk)}>
                {busy ? t("goods.creating") : user ? t("goods.submit") : t("goods.loginToContinue")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            )}
          </div>
        )}
      </div>

      <CountrySearchDialog open={searchOpen} onOpenChange={setSearchOpen} onSelect={(c) => pick(c.name)} />

      {/* Instructions générées après création (fournisseur / proche / moi-même) */}
      {instr && (
        <Dialog open onOpenChange={(v) => { if (!v) { const sid = instr.id; setInstr(null); navigate(`/shipments/${sid}`); } }}>
          <DialogContent data-testid="goods-instructions" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{holder === "supplier" ? t("goods.instrSupplier") : holder === "relative" ? t("goods.instrRelative") : t("goods.instrSelf")}</DialogTitle>
            </DialogHeader>
            <div className="text-sm space-y-1 border border-border bg-secondary/50 p-4" data-testid="goods-instructions-body">
              <p className="font-semibold">{t("goods.instrSend")}</p>
              <p>{instr.hub_name}</p>
              <p>{instr.receiving_address}</p>
              <p className="mt-2"><b>{t("goods.instrRef")}</b> {instr.reference}{user?.client_code ? ` · ${user.client_code}` : ""}</p>
              <p><b>{t("goods.instrDest")}</b> {instr.dest_city || instr.dest_country}</p>
              <p><b>{t("goods.instrMode")}</b> {instr.mode === "air" ? t("search.air") : t("search.sea")}</p>
              <p className="text-muted-foreground">{t("goods.instrTrack")}</p>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button data-testid="instr-copy" variant="outline" className="rounded-sm" onClick={() => {
                const txt = `${t("goods.instrSend")}\n${instr.hub_name}\n${instr.receiving_address}\n${t("goods.instrRef")} ${instr.reference}${user?.client_code ? " · " + user.client_code : ""}\n${t("goods.instrDest")} ${instr.dest_city || instr.dest_country}\n${t("goods.instrMode")} ${instr.mode === "air" ? t("search.air") : t("search.sea")}\n${t("goods.instrTrack")}`;
                navigator.clipboard.writeText(txt); toast.success(t("goods.copied"));
              }}>{t("goods.copy")}</Button>
              <Button data-testid="instr-share" variant="outline" className="rounded-sm" onClick={() => {
                const txt = `${t("goods.instrSend")}\n${instr.hub_name}\n${instr.receiving_address}\n${t("goods.instrRef")} ${instr.reference}${user?.client_code ? " · " + user.client_code : ""}\n${t("goods.instrDest")} ${instr.dest_city || instr.dest_country}\n${t("goods.instrMode")} ${instr.mode === "air" ? t("search.air") : t("search.sea")}\n${t("goods.instrTrack")}`;
                if (navigator.share) navigator.share({ text: txt }).catch(() => {});
                else window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
              }}>{t("goods.share")}</Button>
              <Button data-testid="instr-view" className="rounded-sm bg-accent text-white" onClick={() => navigate(`/shipments/${instr.id}`)}>{t("goods.viewShipment")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog article non répertorié */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent data-testid="goods-custom-dialog">
          <DialogHeader><DialogTitle>{t("goods.customTitle")}</DialogTitle><DialogDescription>{t("goods.customDesc2")}</DialogDescription></DialogHeader>
          {mode === "sea" && Number(cbmRate?.rate) > 0 && (
            <div className="border border-accent/40 bg-accent/5 px-3 py-2 text-sm font-semibold text-accent rounded-sm" data-testid="goods-custom-tariff">
              Tarif maritime applicable : {fmt(cbmRate.rate)} {curSym(cbmRate.currency)}/m³
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("goods.customName")}</Label><Input data-testid="goods-custom-name" className="rounded-sm" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} /></div>
            <div><Label>{t("goods.customQty")}</Label><Input data-testid="goods-custom-qty" type="number" className="rounded-sm" value={custom.qty} onChange={(e) => setCustom({ ...custom, qty: e.target.value })} /></div>
            <div><Label>{t("goods.customWeight")}</Label><Input data-testid="goods-custom-weight" type="number" className="rounded-sm" value={custom.weight_est} onChange={(e) => setCustom({ ...custom, weight_est: e.target.value })} /></div>
            <div className="col-span-2"><Label>Type de récipient</Label>
              <select data-testid="goods-custom-container" className="w-full h-9 border border-border bg-card px-2 text-sm rounded-sm" value={custom.container} onChange={(e) => setCustom({ ...custom, container: e.target.value })}>
                <option value="">— Choisir —</option>
                <option value="carton">Carton</option><option value="fut">Fût</option><option value="valise">Valise</option><option value="sac">Sac</option><option value="caisse">Caisse</option><option value="autre">Autre</option>
              </select></div>
            {/* Dimensions facultatives → calcul automatique au tarif CBM du trajet ; sinon Devis requis */}
            <div className="col-span-2 text-xs font-semibold mt-1">Dimensions de votre article (cm) <span className="text-muted-foreground font-normal">— si nécessaire</span></div>
            <div><Label>Longueur (cm)</Label><Input data-testid="goods-custom-length" type="number" className="rounded-sm" value={custom.length_cm} onChange={(e) => setCustom({ ...custom, length_cm: e.target.value })} /></div>
            <div><Label>Largeur (cm)</Label><Input data-testid="goods-custom-width" type="number" className="rounded-sm" value={custom.width_cm} onChange={(e) => setCustom({ ...custom, width_cm: e.target.value })} /></div>
            <div><Label>Hauteur (cm)</Label><Input data-testid="goods-custom-height" type="number" className="rounded-sm" value={custom.height_cm} onChange={(e) => setCustom({ ...custom, height_cm: e.target.value })} /></div>
            {/* Calcul en direct : Volume × tarif CBM du trajet ; sinon Devis requis */}
            <div className="col-span-2 border border-border bg-secondary/50 px-3 py-2 text-sm rounded-sm space-y-0.5" data-testid="goods-custom-calc">
              {(() => {
                const L = Number(custom.length_cm) || 0, W = Number(custom.width_cm) || 0, H = Number(custom.height_cm) || 0;
                const qn = Math.max(1, Number(custom.qty) || 1);
                const rate = mode === "sea" ? Number(cbmRate?.rate) || 0 : 0;
                if (L > 0 && W > 0 && H > 0 && rate > 0) {
                  const vol = Math.round((L * W * H) / 1e6 * qn * 10000) / 10000;
                  const amt = Math.round(vol * rate * 100) / 100;
                  const sym = curSym(cbmRate.currency);
                  return (<>
                    <p data-testid="goods-custom-volume">Volume calculé : <b>{fmt(vol)} m³</b></p>
                    <p className="text-muted-foreground" data-testid="goods-custom-formula">Calcul : {fmt(vol)} m³ × {fmt(rate)} {sym}/m³</p>
                    <p className="font-semibold text-accent" data-testid="goods-custom-indicative">Estimation indicative : {fmt(amt)} {sym}</p>
                  </>);
                }
                return <p className="font-semibold text-amber-700" data-testid="goods-custom-quote">Devis requis</p>;
              })()}
            </div>
            <div className="col-span-2"><Label>{t("goods.customDescLabel")}</Label><Textarea className="rounded-sm" value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="goods-custom-submit" className="rounded-sm bg-accent text-white" onClick={addCustom}>{t("goods.customSubmit")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
